import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import type { PurchaseReceiptLineSummary } from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { LoadingState } from '@/components/ui/LoadingState';
import { PageHeader } from '@/components/ui/PageHeader';
import { useCurrency } from '@/hooks/useCurrency';
import { apiErrorMessage } from '@/services/apiClient';
import { usePurchaseReceiptQuery } from '../api/purchase-receipts.api';

function Detail({ label, value, ltr = false }: { label: string; value: string; ltr?: boolean }) {
  return (
    <div className="space-y-0.5">
      <dt className="text-muted text-xs">{label}</dt>
      <dd className="text-ink text-sm" dir={ltr ? 'ltr' : 'auto'}>
        {value}
      </dd>
    </div>
  );
}

/**
 * One delivery, exactly as it was counted in.
 *
 * Read-only, because a posted receipt is history: there is no edit and no delete, and a mistake is
 * corrected by a later movement rather than by rewriting what the loading bay recorded. The item
 * names are the order's own, frozen when it was placed, so a later catalogue rename leaves this
 * page reading as it did on the day.
 */
export function GoodsReceiptDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { receiptId } = useParams<{ receiptId: string }>();
  const { format } = useCurrency();

  const { data, isPending, isError, error, refetch } = usePurchaseReceiptQuery(receiptId);

  if (isPending) return <LoadingState />;
  if (isError || !data) {
    return (
      <div className="space-y-3">
        <Alert tone="error">{apiErrorMessage(error) ?? t('purchasing.receipts.loadError')}</Alert>
        <Button variant="secondary" onClick={() => void refetch()}>
          {t('common.retry')}
        </Button>
      </div>
    );
  }

  const columns: Column<PurchaseReceiptLineSummary>[] = [
    {
      key: 'item',
      header: t('purchasing.orders.fields.item'),
      primary: true,
      render: (line) => (
        <span className="flex flex-col">
          <span className="text-ink text-sm" dir="auto">
            {line.variantName ?? line.productName}
          </span>
          <span className="text-muted font-mono text-xs" dir="ltr">
            {line.sku}
          </span>
        </span>
      ),
    },
    {
      key: 'lot',
      header: t('purchasing.receipts.fields.lotNumber'),
      render: (line) =>
        line.lotNumber ? (
          <span className="flex flex-col text-xs">
            <span className="text-ink font-mono" dir="ltr">
              {line.lotNumber}
            </span>
            {line.expiryDate ? (
              <span className="text-muted" dir="ltr">
                {t('purchasing.receipts.fields.expires', { date: line.expiryDate })}
              </span>
            ) : null}
          </span>
        ) : (
          <span className="text-muted">—</span>
        ),
    },
    {
      key: 'quantity',
      header: t('purchasing.receipts.fields.quantity'),
      align: 'end',
      render: (line) => (
        <span className="text-ink font-medium" dir="ltr">
          {line.quantityReceived}
        </span>
      ),
    },
    {
      key: 'billed',
      header: t('purchasing.invoices.invoicedOnOrder'),
      align: 'end',
      render: (line) => (
        <span className="flex flex-col items-end text-xs">
          <span className="text-ink" dir="ltr">
            {line.quantityInvoiced}
          </span>
          {line.quantityInvoiceable > 0 ? (
            <span className="text-muted" dir="ltr">
              {t('purchasing.invoices.leftToBill', { count: line.quantityInvoiceable })}
            </span>
          ) : null}
        </span>
      ),
    },
    {
      key: 'cost',
      header: t('purchasing.orders.fields.agreedUnitCost'),
      align: 'end',
      render: (line) => (
        <span className="text-muted" dir="ltr">
          {format(line.agreedUnitCostMinor)}
        </span>
      ),
    },
    {
      key: 'total',
      header: t('purchasing.orders.fields.lineTotal'),
      align: 'end',
      render: (line) => (
        <span className="flex flex-col text-xs">
          <span className="text-ink" dir="ltr">
            {format(line.totalMinor)}
          </span>
          <span className="text-muted" dir="ltr">
            {t('purchasing.orders.fields.netAndTax', {
              net: format(line.netMinor),
              tax: format(line.taxMinor),
            })}
          </span>
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={data.reference}
        subtitle={t('purchasing.receipts.detailSubtitle', {
          reference: data.purchaseOrderReference,
        })}
        actions={
          <Button
            variant="secondary"
            onClick={() => void navigate(`/purchasing/orders/${data.purchaseOrderId}`)}
          >
            {t('purchasing.orders.backToOrder')}
          </Button>
        }
      />

      <Alert tone="info">{t('purchasing.receipts.readOnly')}</Alert>

      <Card>
        <CardHeader title={t('purchasing.receipts.detailSection')} />
        <CardBody>
          <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Detail label={t('purchasing.orders.fields.supplier')} value={data.supplierName} />
            <Detail label={t('purchasing.orders.fields.branch')} value={data.branchName} />
            <Detail
              label={t('purchasing.receipts.fields.receivedOn')}
              value={data.receivedOn}
              ltr
            />
            <Detail label={t('purchasing.orders.fields.currency')} value={data.currencyCode} ltr />
            <Detail
              label={t('purchasing.receipts.fields.deliveryReference')}
              value={data.supplierDeliveryReference ?? '—'}
            />
            <Detail
              label={t('purchasing.receipts.fields.receivedBy')}
              value={data.receivedByName ?? '—'}
            />
            <Detail
              label={t('purchasing.receipts.fields.quantity')}
              value={String(data.totalQuantity)}
              ltr
            />
            <Detail
              label={t('purchasing.orders.fields.total')}
              value={format(data.totalMinor)}
              ltr
            />
          </dl>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title={t('purchasing.receipts.linesSection')}
          subtitle={t('purchasing.orders.taxExclusive')}
        />
        <DataTable
          caption={t('purchasing.receipts.linesSection')}
          columns={columns}
          rows={data.lines}
          rowKey={(line) => line.id}
          emptyTitle={t('purchasing.receipts.noLines')}
        />
      </Card>
    </div>
  );
}
